import React, { ReactElement, useCallback, useEffect } from "react";
import { Settings, WaterLevelSettings, WorkModeSettings } from ".";
import ReactVisBase from "../../components/ReactVisBase";
import { getLanguage } from "../../helpers/init";
import { MemoizedValue } from "../../helpers/memoizer";
import { OrgStruct } from "../../helpers/org-struct";
import { ParamsWatcher, writeParam } from "../../helpers/params";
import { VisState } from "../../helpers/vis";
import { VisDataFetcher } from "../../helpers/visdata";
import useMemoizedParamsData from "../../hooks/use-memoized-params-data";
import { Settings as VisScheduleSettings } from "../light-schedule";
import { VisSmart as VisSchedule } from "../light-schedule/Vis";
import "./Vis.css";
import VisContentWithChrome, { VisContentProps } from "./components/VisContent";
import oc from "open-color";
import { WorkModeInfo } from "./components/WorkModeSection";
import { ReportsAlertsHeaderProps, VisReport } from "./components/ReportsAlertsHeader";
import {
  convertDateToFormatedDateStr,
  deductColorFromSettings,
  isMarkerValueMatchCondition,
} from "../../helpers/utils";
import { MaintenanceDateProps } from "./components/MaintenanceDate";

export default function Vis({ state }: { state: VisState<Settings> }): ReactElement {
  return (
    <ReactVisBase className="vis-pumping" state={state} style={{ height: "100%" }}>
      <ReadyStateVis state={state} />
    </ReactVisBase>
  );
}

export function ReadyStateVis({ state }: { state: VisState<Settings> }): ReactElement {
  const props = useContentProps(state);
  return <VisContentWithChrome {...props} />;
}

export type WatchedParamKey =
  | "pumpState"
  | `workMode${number}Marker`
  | "manualPumpSwitchMarker"
  | `wm${number}AddControl${number}Value`
  | "waterLevel"
  | `discreteWaterLevel${number}`
  | "pressure"
  | "flow"
  | "voltagePhase1"
  | "voltagePhase2"
  | "voltagePhase3"
  | "currentPhase1"
  | "currentPhase2"
  | "currentPhase3"
  | `additionalValue${number}`
  | `counter${number}`
  | `generalAlarm`
  | `alarm${number}Marker`
  | `alarm${number}AdditionalValue`
  | "faultMarker";

function getWorkModesList(
  settings: WorkModeSettings[],
  watcher: ParamsWatcher,
  orgStruct: OrgStruct,
  setShowSpinner: (value: boolean) => void,
): WorkModeInfo[] {
  return settings.map((wm, i) => ({
    key: i.toString(),
    label: wm.name,
    hideManualSwitch: wm.hideManualSwitch || false,
    showScheduleSetup: wm.showScheduleSetup || false,
    onActivate: useCallback(
      () =>
        activateWorkMode(settings, i, setShowSpinner, orgStruct, watcher).catch((err) => {
          alert(`Work mode activation error: ${(err as Error).message}`);
        }),
      [settings, i, setShowSpinner, orgStruct, watcher],
    ),
  }));
}

async function activateWorkMode(
  settings: WorkModeSettings[],
  i: number,
  setShowSpinner: (value: boolean) => void,
  orgStruct: OrgStruct,
  watcher: ParamsWatcher,
): Promise<void> {
  setShowSpinner(true);

  const wm = settings[i];

  // running commands one by one
  for (const command of wm.commands) {
    const paramId = orgStruct.getId(command.paramPath);
    if (!paramId) {
      throw new Error(`Param ${command.paramPath} not found`);
    }
    await writeParam(paramId, command.value.toString());
  }

  // updating all markers to get the current work mode
  const markers: `workMode${number}Marker`[] = [];
  for (let i = 0; i < settings.length; i++) {
    markers.push(`workMode${i}Marker`);
  }
  await watcher.readManyNowFromDevice(markers);

  setShowSpinner(false);
}

function getCurrentWorkModeKey(
  settings: Settings["workModes"],
  data: Partial<Record<WatchedParamKey, MemoizedValue>>,
): string | undefined {
  let currentWorkModeKey: string | undefined;
  for (let i = 0; i < settings.length; i++) {
    if (data[`workMode${i}Marker`]?.displayValue === settings[i].marker.value.toString()) {
      currentWorkModeKey = i.toString();
      break;
    }
  }
  return currentWorkModeKey;
}

export function useContentProps(state: VisState<Settings>): VisContentProps & { name?: string } {
  const [name, setName] = React.useState<string | undefined>(undefined);
  const [_netSlug, setNetSlug] = React.useState<string | undefined>(undefined);

  useEffect(() => {
    const defaultNet = state.org.getDefaultNet();
    setNetSlug(defaultNet?.slug);
    setName(state.settings.shortName || defaultNet?.name || undefined);

    const keys = ["pumpState"];
    if (state.settings?.generalAlarm) {
      keys.push("generalAlarm");
    }
    if (state.settings?.workModes.length) {
      keys.push(...state.settings.workModes.map((_w, i) => `workMode${i}Marker`));
    }
    state.withData(keys, (data) => {
      let currentWorkModeName: string | undefined;
      for (let i = 0; i < settings.workModes.length; i++) {
        if (data[`workMode${i}Marker`] === settings.workModes[i].marker.value.toString()) {
          currentWorkModeName = settings.workModes[i].name;
          break;
        }
      }

      const pumpStateStatuses = deductCurrentPumpStateStatuses(
        state.settings,
        data.pumpState,
        data.generalAlarm,
        currentWorkModeName,
      );
      state.data.set("hasConnection", `${pumpStateStatuses.hasConnection}`);
      state.data.set("pumpIsOn", `${pumpStateStatuses.isOn}`);
      state.data.set("hasAlarm", `${pumpStateStatuses.hasAlarm}`);
      pumpStateStatuses.workMode && state.data.set("workMode", `${pumpStateStatuses.workMode}`);
      pumpStateStatuses.shortStateName &&
        state.data.set("shortStateName", `${pumpStateStatuses.shortStateName}`);
    });
  }, []);

  const [showSpinner, setShowSpinner] = React.useState(false);
  const watcher = state.params;
  const settings = state.settings;
  const updatePeriodSec = settings.updatePeriodSec;

  // set watched params
  // TODO_FUTURE: add faultreset
  const watchedParams = prepareWatchedParams(state.settings);

  const params: [
    storeKey: WatchedParamKey,
    path: string,
    update: number,
    priority?: "low" | "normal" | "high",
  ][] = [];
  for (const [key, param] of Object.entries(watchedParams)) {
    if (param) {
      params.push([key as WatchedParamKey, param[0], updatePeriodSec, param[1]]);
    }
  }
  const [data, isReady] = useMemoizedParamsData(state.memoizer, watcher, getLanguage(), params);

  const currentAlarm = getCurrentAlarm(settings, data);

  const workModesList = getWorkModesList(settings.workModes, watcher, state.org, setShowSpinner);
  const currentWorkModeKey = getCurrentWorkModeKey(settings.workModes, data);

  const manualPumpSwitch = settings.manualPumpSwitch;

  const currentWaterLevel = settings.water?.level
    ? getCurrentWaterLevel(data, settings.water.level)
    : undefined;

  state.settings.scheduleTableStartAddress &&
    ((state.settings as unknown as { startAddress: number }).startAddress =
      state.settings.scheduleTableStartAddress);

  return {
    // ...makeVisContentPropsMock(state),
    pumpState: {
      hasConnection: state.data.get("hasConnection") === "true",
      isOn: state.data.get("pumpIsOn") === "true",
      hasAlarm: state.data.get("hasAlarm") === "true",
      workMode: state.data.get("workMode"),
    },
    name,
    showSpinner: !isReady || showSpinner,
    waterLevel:
      settings.showWaterLevelGraph && settings.water?.level
        ? {
            value: Number(currentWaterLevel?.displayValue || "0"),
            max: settings.water.level.max,
            unit: settings.water.level.unit,
          }
        : undefined,
    mainValues: getMainValues(settings, data),
    additionalValues: getCurrentAdditionalValues(data, settings.additionalValues),
    includeInteractiveElements: state.settings.showInteractiveElements,
    shortFormatForCounters: state.settings.useShortFormatForCounters,
    lastUpdateOn: state.memoizer.lastUpdate,
    workModesList,
    currentWorkModeKey,
    manualBtnIsOn: settings.manualPumpSwitch
      ? isMarkerValueMatchCondition(
          settings.manualPumpSwitch.markerOnValue,
          settings.manualPumpSwitch.compareOperation,
          data.manualPumpSwitchMarker?.displayValue,
        )
      : false,
    manualSwitchPumpOnHandler: manualPumpSwitch
      ? useCallback(
          () =>
            manualSwitchPumpHandler(
              manualPumpSwitch.onCommands,
              state.org,
              state.params,
              setShowSpinner,
            ).catch((err) => {
              alert(`Pump switch on error: ${(err as Error).message}`);
            }),
          [state],
        )
      : undefined,
    manualSwitchPumpOffHandler: manualPumpSwitch
      ? useCallback(
          () =>
            manualSwitchPumpHandler(
              manualPumpSwitch.offCommands,
              state.org,
              state.params,
              setShowSpinner,
            ).catch((err) => {
              alert(`Pump switch off error: ${(err as Error).message}`);
            }),
          [state],
        )
      : undefined,
    countersData: getCountersData(settings, data),
    currentAlarm,
    additionalControlFormContents: getAdditionalControlFormContents(
      settings,
      data,
      state,
      setShowSpinner,
    ),
    isScheduletableSetting: !!state.settings.scheduleTableStartAddress,
    visSchedule: <VisSchedule state={state as VisState<VisScheduleSettings>} />,
    // openScheduleSetup: useCallback(() => {
    //   if (!state.settings.scheduleTableStartAddress) {
    //     return;
    //   }
    //   (state.settings as unknown as { startAddress: number }).startAddress =
    //     state.settings.scheduleTableStartAddress;
    //   await state.modal.show({
    //     title: t("Weekly schedule", "Настройка тижневого графіку"),
    //     width: "800px",
    //     content: <VisSchedule state={state as VisState<VisScheduleSettings>} />,
    //   });
    // }, []),
    reportsProps: getReportsProps(state.visData, state.params),
    showReports: settings.showReports,
    refs: state.refs,
    pumpIndicationSetup: () => {
      pumpIndicationSetup(state);
    },
    showMaintenanceDate: settings.showMaintenanceDate,
    maintenanceDateProps: getMaintenanceDateProps(state.visData),
  };
}

export interface PumpStateStatuses {
  hasConnection: boolean;
  isOn: boolean;
  hasAlarm: boolean;
  workMode?: string;
  shortStateName?: string;
}

export function deductCurrentPumpStateStatuses(
  settings: Readonly<Settings>,
  pumpStateValue: string | undefined,
  alarmValue: string | undefined,
  currentWorkModeName: string | undefined,
): PumpStateStatuses {
  const rawPumpStateStatuses: Omit<PumpStateStatuses, "shortStateName"> = {
    hasConnection: false,
    isOn: false,
    hasAlarm: false,
  };

  rawPumpStateStatuses.hasConnection = !(
    pumpStateValue === "" ||
    pumpStateValue === undefined ||
    pumpStateValue === "NORT"
  );
  rawPumpStateStatuses.isOn = isMarkerValueMatchCondition(
    settings.pumpState.onValue,
    settings.pumpState.compareOperation,
    pumpStateValue,
  );
  rawPumpStateStatuses.hasAlarm = settings.generalAlarm
    ? isMarkerValueMatchCondition(
        settings.generalAlarm.alarmMarkerValue,
        settings.generalAlarm.compareOperation,
        alarmValue,
      )
    : false;
  rawPumpStateStatuses.workMode = currentWorkModeName;

  let shortStateName;
  for (const shortState of settings.shortStates) {
    const { name: _, ...shortStateWithoutName } = shortState;
    if (
      (Object.keys(shortStateWithoutName) as (keyof typeof shortStateWithoutName)[]).every(
        (key) => {
          return (
            Object.prototype.hasOwnProperty.call(rawPumpStateStatuses, key) &&
            shortStateWithoutName[key] === rawPumpStateStatuses[key]
          );
        },
      )
    ) {
      shortStateName = shortState.name;
      break;
    }
  }

  return {
    ...rawPumpStateStatuses,
    shortStateName,
  };
}

function prepareWatchedParams(
  settings: Readonly<Settings>,
): Partial<Record<WatchedParamKey, [path: string, priority: "low" | "normal" | "high"]>> {
  const watchedParams: Partial<
    Record<WatchedParamKey, [path: string, priority: "low" | "normal" | "high"]>
  > = {
    pumpState: [settings.pumpState.paramPath, "high"],
    pressure: settings.water?.pressure && [settings.water?.pressure.paramPath, "normal"],
    flow: settings.water?.flow && [settings.water?.flow.paramPath, "normal"],
    manualPumpSwitchMarker: settings.manualPumpSwitch && [
      settings.manualPumpSwitch?.markerParamPath,
      "normal",
    ],
    voltagePhase1: settings.voltage?.phase1ParamPath
      ? [settings.voltage.phase1ParamPath, "normal"]
      : undefined,
    voltagePhase2: settings.voltage?.phase2ParamPath
      ? [settings.voltage.phase2ParamPath, "normal"]
      : undefined,
    voltagePhase3: settings.voltage?.phase3ParamPath
      ? [settings.voltage.phase3ParamPath, "normal"]
      : undefined,
    currentPhase1: settings.current?.phase1ParamPath
      ? [settings.current.phase1ParamPath, "high"]
      : undefined,
    currentPhase2: settings.current?.phase2ParamPath
      ? [settings.current.phase2ParamPath, "high"]
      : undefined,
    currentPhase3: settings.current?.phase3ParamPath
      ? [settings.current.phase3ParamPath, "high"]
      : undefined,
    generalAlarm: settings.generalAlarm?.paramPath
      ? [settings.generalAlarm?.paramPath, "high"]
      : undefined,
  };
  for (let i = 0; i < settings.workModes.length; i++) {
    watchedParams[`workMode${i}Marker`] = [settings.workModes[i].marker.paramPath, "normal"];
    if (settings.workModes[i].additionalControls?.length) {
      const length = settings.workModes[i].additionalControls.length;
      for (let j = 0; j < length; j++) {
        watchedParams[`wm${i}AddControl${j}Value`] = [
          settings.workModes[i].additionalControls[j].paramPath,
          "normal",
        ];
      }
    }
  }
  for (let i = 0; i < settings.additionalValues.length; i++) {
    watchedParams[`additionalValue${i}`] = [settings.additionalValues[i].paramPath, "normal"];
  }
  for (let i = 0; i < settings.counters.length; i++) {
    watchedParams[`counter${i}`] = [settings.counters[i].paramPath, "normal"];
  }
  for (let i = 0; i < settings.alarms.length; i++) {
    watchedParams[`alarm${i}Marker`] = [settings.alarms[i].alarmMarkerParamPath, "high"];
  }
  if (settings.water?.level) {
    if (settings.water.level.value.kind === "numeric") {
      watchedParams.waterLevel = [settings.water.level.value.paramPath, "high"];
    } else {
      for (let i = 0; i < settings.water.level.value.levels.length; i++) {
        watchedParams[`discreteWaterLevel${i}`] = [
          settings.water.level.value.levels[i].bitParamPath,
          "high",
        ];
      }
    }
  }

  return watchedParams;
}

function pumpIndicationSetup(state: VisState<Settings>): void {
  const stateColors = Object.fromEntries(
    Object.entries(state.settings.shortStatesColors).map(([key, value]) => [
      key,
      deductColorFromSettings(value),
    ]),
  );
  state.connectStyle("pumpStroke", "shortStateName", "stroke", oc.gray[5], stateColors);
  state.connectStyle("pumpText", "shortStateName", "fill", oc.gray[5], stateColors);
}

function getReportsProps(
  visData: VisDataFetcher,
  watcher: ParamsWatcher,
): ReportsAlertsHeaderProps {
  const rawReports = visData.get("reports");
  const reports = rawReports ? (JSON.parse(rawReports) as VisReport[]) : [];
  const generalReportParamsIds: number[] = [];
  for (const key of ["pressure", "currentPhase1", "currentPhase2", "currentPhase3"]) {
    if (!watcher.getWatchingStoreKeys().includes(key)) {
      continue;
    }
    generalReportParamsIds.push(watcher.id(key));
  }
  return {
    reports,
    saveReportsHandler: useCallback(
      (newReports) =>
        saveReports(visData, newReports).catch((err) =>
          alert(`Reports save error: ${(err as Error).message}`),
        ),
      [visData],
    ),
    generalReportParamsIds,
  };
}

async function saveReports(visData: VisDataFetcher, reports: VisReport[]): Promise<void> {
  await visData.save("reports", JSON.stringify(reports));
}

function getMaintenanceDateProps(visData: VisDataFetcher): MaintenanceDateProps {
  const maintenanceDateStr = visData.get("maintenceDate");

  return {
    maintenanceDateStr: maintenanceDateStr || "",
    saveMaintenanceDate: useCallback(
      async (newDateStr: string) => {
        const date = new Date(newDateStr);
        if (newDateStr !== "" && isNaN(Number(date.getTime()))) {
          throw new Error("Maintence date entered incorrectly.");
        }
        await visData.save("maintenceDate", !newDateStr ? "" : convertDateToFormatedDateStr(date));
      },
      [visData],
    ),
  };
}

function getMainValues(
  settings: Readonly<Settings>,
  data: Readonly<Partial<Record<WatchedParamKey, MemoizedValue>>>,
): VisContentProps["mainValues"] {
  const currentWaterLevel = settings.water?.level
    ? getCurrentWaterLevel(data, settings.water.level)
    : undefined;
  return {
    water: settings.water
      ? {
          level:
            settings.water.level && currentWaterLevel
              ? {
                  value: currentWaterLevel,
                  unit: settings.water.level.unit,
                }
              : undefined,
          pressure:
            settings.water.pressure && data.pressure
              ? {
                  value: data.pressure,
                  unit: settings.water.pressure.unit,
                }
              : undefined,
          flow:
            settings.water.flow && data.flow
              ? {
                  value: data.flow,
                  unit: settings.water.flow.unit,
                }
              : undefined,
        }
      : undefined,
    voltage: settings.voltage
      ? {
          p1: data.voltagePhase1 && { value: data.voltagePhase1, unit: "V" },
          p2: data.voltagePhase2 && { value: data.voltagePhase2, unit: "V" },
          p3: data.voltagePhase3 && { value: data.voltagePhase3, unit: "V" },
        }
      : undefined,
    current: settings.current
      ? {
          p1: data.currentPhase1 && { value: data.currentPhase1, unit: "A" },
          p2: data.currentPhase2 && { value: data.currentPhase2, unit: "A" },
          p3: data.currentPhase3 && { value: data.currentPhase3, unit: "A" },
        }
      : undefined,
  };
}

function getCurrentAlarm(
  settings: Readonly<Settings>,
  data: Readonly<Partial<Record<WatchedParamKey, MemoizedValue>>>,
): VisContentProps["currentAlarm"] {
  for (let i = 0; i < settings.alarms.length; i++) {
    const value = data[`alarm${i}Marker`]?.displayValue;
    const alarmSettings = settings.alarms[i];
    if (value === alarmSettings.alarmMarkerValue.toString()) {
      return {
        label: alarmSettings.name,
        value: alarmSettings.alarmMarkerValue.toString(),
        isCritical: alarmSettings.isCritical,
      };
    }
  }
  return undefined;
}

function getCountersData(
  settings: Readonly<Settings>,
  data: Readonly<Partial<Record<WatchedParamKey, MemoizedValue>>>,
): VisContentProps["countersData"] {
  const result: VisContentProps["countersData"] = [];
  for (let i = 0; i < settings.counters.length; i++) {
    result.push({
      name: settings.counters[i].name,
      value: data[`counter${i}`],
    });
  }
  return result;
}

function getAdditionalControlFormContents(
  settings: Readonly<Settings>,
  data: Readonly<Partial<Record<WatchedParamKey, MemoizedValue>>>,
  state: VisState<Settings>,
  setShowSpinner: (value: boolean) => void,
): VisContentProps["additionalControlFormContents"] {
  const result: VisContentProps["additionalControlFormContents"] = [];
  for (let i = 0; i < settings.workModes.length; i++) {
    if (settings.workModes[i].additionalControls?.length) {
      const length = settings.workModes[i].additionalControls.length;
      for (let j = 0; j < length; j++) {
        const value = Number(data[`wm${i}AddControl${j}Value`]?.displayValue || "0");
        const formSettings = settings.workModes[i].additionalControls[j];
        result.push({
          name: formSettings.name,
          value,
          unit: formSettings.unit,
          onSubmit: useCallback(
            // eslint-disable-next-line @typescript-eslint/no-loop-func
            () =>
              saveAdditionalControlValue(
                formSettings.storeCommands,
                state.org,
                setShowSpinner,
              ).catch((err) => {
                alert(`Additional control value save error: ${(err as Error).message}`);
              }),
            [formSettings, state],
          ),
        });
      }
    }
  }
  return result;
}

async function saveAdditionalControlValue(
  storeCommands: {
    paramPath: string;
    value: number;
  }[],
  org: OrgStruct,
  setShowSpinner: (value: boolean) => void,
): Promise<void> {
  setShowSpinner(true);
  if (!storeCommands) {
    return;
  }
  for (const command of storeCommands) {
    await writeParamPath(command.paramPath, command.value, org);
  }
  setShowSpinner(false);
}

async function manualSwitchPumpHandler(
  commands: { paramPath: string; value: number }[],
  org: OrgStruct,
  watcher: ParamsWatcher,
  setShowSpinner: (value: boolean) => void,
): Promise<void> {
  setShowSpinner(true);
  for (const command of commands) {
    await writeParamPath(command.paramPath, command.value, org);
  }
  await watcher.readNowFromDevice("pumpState");
  await watcher.readNowFromDevice("manualPumpSwitchMarker");
  setShowSpinner(false);
}

async function writeParamPath(path: string, value: number, org: OrgStruct): Promise<void> {
  const paramId = org.getId(path);
  if (!paramId) {
    throw new Error(`Param ${path} not found`);
  }
  await writeParam(paramId, value.toString());
}

function getCurrentWaterLevel(
  data: Readonly<Partial<Record<WatchedParamKey, MemoizedValue>>>,
  settings: WaterLevelSettings,
): MemoizedValue | undefined {
  if (settings.value.kind === "numeric") {
    return data.waterLevel;
  }
  let value: MemoizedValue | undefined;
  for (let i = 0; i < settings.value.levels.length; i++) {
    const discreteValue = data[`discreteWaterLevel${i}`];
    if (discreteValue?.displayValue && Number(discreteValue?.displayValue)) {
      value = {
        displayValue: settings.value.levels[i].value.toString(),
        isStale: false,
        isReaded: true,
      };
    }
  }
  return value;
}

function getCurrentAdditionalValues(
  data: Readonly<Partial<Record<WatchedParamKey, MemoizedValue>>>,
  settings: Settings["additionalValues"],
): VisContentProps["additionalValues"] {
  const result: VisContentProps["additionalValues"] = [];

  for (let i = 0; i < settings.length; i++) {
    const value = data[`additionalValue${i}`]?.displayValue;
    result.push({
      name: settings[i].name,
      value: value || "-",
      unit: settings[i].unit,
    });
  }

  return result;
}
